/**
 * Dotability Loader
 * 
 * Lightweight script (~2KB) that customers embed on their site.
 * It loads the full widget bundle (app.js) from CDN asynchronously.
 * 
 * Usage:
 *   <script src="https://cdn.dotability.it/widget/loader.js"
 *           data-api-key="YOUR_API_KEY"
 *           data-position="bottom-left"
 *           data-primary-color="#79272b"
 *           data-language="it">
 *   </script>
 */

export {};

interface LoaderConfig {
  apiKey: string;
  position?: string;
  primaryColor?: string;
  language?: string;
  buttonSize?: string;
  hideMobile?: string;
  hideTrigger?: string;
  triggerSize?: string;
  declarationUrl?: string;
  debug?: string;
}

declare global {
  interface Window {
    __dotability_loader_initialized?: boolean;
    __dotability_config?: LoaderConfig;
    __dotability_cdn_base?: string;
    __dotability_api_base?: string;
  }
}

(function () {
  'use strict';

  // Prevent double-loading
  if (window.__dotability_loader_initialized) return;
  window.__dotability_loader_initialized = true;

  // Read config from script tag data attributes
  const currentScript = document.currentScript as HTMLScriptElement;
  if (!currentScript) {
    console.error('[Dotability] Could not find loader script element');
    return;
  }

  const config: LoaderConfig = {
    apiKey: currentScript.getAttribute('data-api-key') || '',
    position: currentScript.getAttribute('data-position') || undefined,
    primaryColor: currentScript.getAttribute('data-primary-color') || undefined,
    language: currentScript.getAttribute('data-language') || undefined,
    buttonSize: currentScript.getAttribute('data-button-size') || undefined,
    hideMobile: currentScript.getAttribute('data-hide-mobile') || undefined,
    hideTrigger: currentScript.getAttribute('data-hide-trigger') || undefined,
    triggerSize: currentScript.getAttribute('data-trigger-size') || undefined,
    declarationUrl: currentScript.getAttribute('data-declaration-url') || undefined,
    debug: currentScript.getAttribute('data-debug') || undefined,
  };

  if (!config.apiKey) {
    console.error('[Dotability] data-api-key is required');
    return;
  }

  // Determine CDN base URL
  const cdnBase = currentScript.getAttribute('data-cdn') ||
    currentScript.src.replace(/\/loader\.js.*$/, '');

  // Determine API base URL (portal origin)
  const apiBase = currentScript.getAttribute('data-api-base') ||
    currentScript.src.replace(/\/widget\/.*$/, '');

  // Store config globally for app.js to pick up
  window.__dotability_config = config;
  window.__dotability_cdn_base = cdnBase;
  window.__dotability_api_base = apiBase;

  // Load the full widget bundle
  function loadWidget() {
    const styleId = 'dotability-widget-style';
    if (!document.getElementById(styleId)) {
      const stylesheet = document.createElement('link');
      stylesheet.id = styleId;
      stylesheet.rel = 'stylesheet';
      stylesheet.href = `${cdnBase}/app.css`;
      document.head.appendChild(stylesheet);
    }

    const script = document.createElement('script');
    script.src = `${cdnBase}/app.js`;
    script.async = true;

    script.onerror = () => {
      console.error('[Dotability] Failed to load widget bundle');
      // Retry once after 3 seconds
      setTimeout(() => {
        const retry = document.createElement('script');
        retry.src = `${cdnBase}/app.js?retry=1&t=${Date.now()}`;
        retry.async = true;
        retry.onerror = () => {
          console.error('[Dotability] Widget bundle failed to load after retry');
        };
        document.head.appendChild(retry);
      }, 3000);
    };

    document.head.appendChild(script);
  }

  // Load when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadWidget);
  } else {
    loadWidget();
  }
})();
